import React, { createContext, useContext } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  RESOURCE_NODE_THEME,
  TECHNICAL_SPECIALIST_THEME,
  type RosterNodeTheme,
} from 'components/IncidentConfig/rosterOrgChartTheme';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
} from 'components/IncidentConfig/roster/seatBoxMetrics';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type SeatBoxNodeData = {
  positionId: string;
  name: string;
  isWorkgroup: boolean;
  isResourcePosition: boolean;
  /** Kind of the attached resource, shown as the node's badge. */
  resourceType: string | null;
  /** Occupants on the active roster; empty unless the filter includes it. */
  activeMembers: RosterEntry[];
  /**
   * Occupants scheduled on the draft roster; empty unless the filter includes
   * it.
   */
  draftMembers: RosterEntry[];
  /** Resources attached to this seat on the active roster. */
  activeResourceCount: number;
  /** Resources attached to this seat on the draft roster. */
  draftResourceCount: number;
  /**
   * When both rosters are drawn together they need labelling to stay
   * distinguishable.
   */
  labelRosters: boolean;
  /** Join-period label per person, used when All is selected. */
  periodLabels?: Record<string, string>;
  /** Screen-only activation, check-in, and sign-in counts. Hidden on ICS-207. */
  hwcgStatusLine?: string | null;
  /** Flagged to leave the roster when the operational period next advances. */
  retireNextOp: boolean;
  /**
   * ICS organisational element this seat represents, already resolved to a
   * label; null when there is none.
   */
  categoryLabel: string | null;
  sectionTheme: RosterNodeTheme;
  onOpen: (positionId: string) => void;
  onAddChildPosition: (positionId: string) => void;
  onAddChildResource: (positionId: string) => void;
  /** False when the seat is visible but the signed-in user cannot open it. */
  canOpen?: boolean;
  /**
   * Drawn for reference only, as a form box does, with nothing on the seat to
   * act on.
   */
  readOnly: boolean;
  /** Children drawn under this seat before any collapse; 0 hides the chevron. */
  childCount?: number;
  collapsed?: boolean;
  /** Seats hidden beneath a collapsed seat, shown as "+N". */
  hiddenCount?: number;
  onToggleCollapse?: (positionId: string) => void;
};

// Every occupant is drawn in full rather than collapsed to a count, so the layout needs a height per node before
// anything has rendered. These are the rendered heights of each row in the box; the chart corrects them from real
// measurements once the cards have been laid out and measured.
const NODE_CHROME_HEIGHT = 34;
const PERSON_LINE_HEIGHT = 30;
const MINOR_LINE_HEIGHT = 15;

export type RosterChartColorMode = 'dark' | 'light';

export const RosterChartColorModeContext =
  createContext<RosterChartColorMode>('dark');

export function estimateNodeHeight(data: SeatBoxNodeData): number {
  const categoryLines = data.categoryLabel ? 1 : 0;

  if (data.isResourcePosition)
    return NODE_HEIGHT + categoryLines * MINOR_LINE_HEIGHT;

  const occupants = data.activeMembers.length + data.draftMembers.length;
  const statusLines = data.hwcgStatusLine ? 2 : 0;
  const emptyLines = occupants === 0 ? 1 : 0;
  const groupLabels = data.labelRosters
    ? (data.activeMembers.length > 0 ? 1 : 0) +
      (data.draftMembers.length > 0 ? 1 : 0)
    : 0;
  const resourceLines =
    (data.activeResourceCount > 0 ? 1 : 0) +
    (data.draftResourceCount > 0 ? 1 : 0);
  // Workgroups add a "Leader:" heading and a divider rule.
  const workgroupLines = data.isWorkgroup && occupants > 0 ? 2 : 0;

  return Math.max(
    NODE_HEIGHT,
    NODE_CHROME_HEIGHT +
      occupants * PERSON_LINE_HEIGHT +
      (emptyLines +
        groupLabels +
        resourceLines +
        workgroupLines +
        categoryLines +
        statusLines) *
        MINOR_LINE_HEIGHT
  );
}

const SECONDARY_TEXT_CLASS =
  'tw:text-[10px] tw:uppercase tw:tracking-wide tw:text-muted-foreground';

/**
 * One occupant of a seat. The dot replaces the old asterisk marking a member
 * who is currently checked in.
 *
 * Name and email are both shown, and the email wraps rather than truncating,
 * because this box is what gets captured into the ICS-207 and a clipped address
 * is not usable there.
 */
const PersonLine: React.FC<{
  member: RosterEntry;
  periodLabel?: string;
}> = ({ member, periodLabel }) => (
  <div className="tw:w-full tw:leading-tight">
    <div className="tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-1">
      {member.active === 1 && member.activeTime && (
        <span
          className="tw:size-1.5 tw:shrink-0 tw:rounded-full tw:bg-teal-400"
          title="Active"
        />
      )}
      <span className="tw:text-xs tw:font-medium tw:text-foreground">
        {member.userName || member.emailAddress}
      </span>
    </div>
    {periodLabel ? (
      <div className={SECONDARY_TEXT_CLASS}>{periodLabel}</div>
    ) : null}
    {member.emailAddress && member.userName && (
      <div className="tw:w-full tw:break-all tw:text-[9px] tw:text-muted-foreground">
        {member.emailAddress}
      </div>
    )}
  </div>
);

/**
 * Heading that separates the current-period occupants from the next-period ones
 * under the "both" filter.
 */
const RosterGroupLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className={SECONDARY_TEXT_CLASS}>{children}</div>;

const UnfilledLine: React.FC = () => (
  <div className="tw:text-[10px] tw:font-medium tw:italic tw:text-red-600 tw:dark:text-red-300">
    Unassigned
  </div>
);

/**
 * Every occupant of one roster, drawn in full. Nothing is collapsed to a count,
 * because the ICS-207 capture has to name each person.
 */
const OccupantGroup: React.FC<{
  positionId: string;
  isWorkgroup: boolean;
  members: RosterEntry[];
  periodLabels?: Record<string, string>;
}> = ({ positionId, isWorkgroup, members, periodLabels }) => {
  const isLeader = (member: RosterEntry) =>
    !!member.incidentPositions?.find((p) => p.positionId === positionId)
      ?.isLeader;

  const leaders = isWorkgroup ? members.filter(isLeader) : [];
  const nonLeaders = isWorkgroup
    ? members.filter((m) => !isLeader(m))
    : members;

  return (
    <>
      {leaders.length > 0 && (
        <div className={SECONDARY_TEXT_CLASS}>
          {leaders.length > 1 ? 'Leaders:' : 'Leader:'}
        </div>
      )}
      {leaders.map((member) => (
        <PersonLine
          key={`leader-${member.userId}`}
          member={member}
          periodLabel={periodLabels?.[member.userId]}
        />
      ))}
      {leaders.length > 0 && nonLeaders.length > 0 && (
        <div className="tw:my-0.5 tw:h-px tw:w-full tw:bg-border" />
      )}
      {nonLeaders.map((member) => (
        <PersonLine
          key={member.userId}
          member={member}
          periodLabel={periodLabels?.[member.userId]}
        />
      ))}
    </>
  );
};

export type SeatBoxCardProps = {
  data: SeatBoxNodeData;
  /** Drawn with a ring; the chart's selection, not the card's own state. */
  selected?: boolean;
};

/**
 * One position in the org chart.
 *
 * A seat is a 230px box on a canvas rather than a page card, so most of Card's
 * scale is overridden. The section colour, the box's size and `--card-spacing`
 * are set inline: the first two are dynamic, and the last has to beat an
 * arbitrary-property class that sets the same variable.
 */
export const SeatBoxCard: React.FC<SeatBoxCardProps> = ({
  data,
  selected = false,
}) => {
  const colorMode = useContext(RosterChartColorModeContext);
  const {
    positionId,
    name,
    isWorkgroup,
    isResourcePosition,
    resourceType,
    activeMembers,
    draftMembers,
    activeResourceCount,
    draftResourceCount,
    labelRosters,
    periodLabels,
    hwcgStatusLine,
    retireNextOp,
    categoryLabel,
    sectionTheme,
    onOpen,
    onAddChildPosition,
    onAddChildResource,
    canOpen = true,
    readOnly,
    childCount = 0,
    collapsed = false,
    hiddenCount = 0,
    onToggleCollapse,
  } = data;
  const showCollapse = !readOnly && childCount > 0 && !!onToggleCollapse;

  // Resources sit outside the ICS section colour scheme, so they stay neutral instead of inheriting the colour of
  // whichever section they happen to report into.
  let nodeTheme = isResourcePosition ? RESOURCE_NODE_THEME : sectionTheme;
  if (!isResourcePosition && name?.includes('Technical Specialist'))
    nodeTheme = TECHNICAL_SPECIALIST_THEME;

  // A seat nobody is sitting in is the one thing worth spotting from across the chart, so it breaks the section
  // colour with a dashed red outline. Resources are never "unfilled" — the resource itself is the occupant.
  const isUnfilled =
    !isResourcePosition && activeMembers.length + draftMembers.length === 0;

  const showAddChild = !isResourcePosition && !readOnly && canOpen;
  const hasOccupantBody = !isResourcePosition;

  const bodyOpensModal = !readOnly && canOpen;

  const cardStyle = {
    '--card-spacing': '0.5rem',
    borderColor: isUnfilled ? 'var(--error-400)' : nodeTheme.border,
    background: nodeTheme.surface,
    width: NODE_WIDTH,
    minHeight: NODE_HEIGHT,
    // The add-child button, the resource badge and the retiring badge all sit
    // outside the card's edges, so it must not clip its own children.
    overflow: 'visible',
    boxSizing: 'border-box',
  } as React.CSSProperties;

  return (
    <Card
      className={cn(
        'tw:relative tw:gap-1 tw:rounded-md tw:border-2 tw:py-1.5 tw:text-center tw:text-foreground tw:shadow-md tw:transition-shadow tw:hover:shadow-lg',
        isUnfilled && 'tw:border-dashed',
        bodyOpensModal && 'tw:cursor-pointer',
        selected
          ? 'tw:ring-2 tw:ring-sky-400 tw:ring-offset-2 tw:ring-offset-background'
          : 'tw:ring-0'
      )}
      title={isWorkgroup ? 'Workgroup' : undefined}
      style={cardStyle}
      onClick={
        bodyOpensModal
          ? (event) => {
              // The add-child menu is portalled to <body>; its item clicks
              // bubble here through React's tree without being on the card.
              // Only a click physically on the card opens the seat.
              if (!event.currentTarget.contains(event.target as Node)) return;
              onOpen(positionId);
            }
          : undefined
      }
    >
      {showAddChild ? (
        <div className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-0 tw:z-20 tw:-translate-x-full tw:-translate-y-1/2 tw:pr-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="tw:pointer-events-auto tw:h-6 tw:w-6 tw:rounded-full tw:border-border tw:bg-background tw:shadow-sm tw:hover:bg-accent"
                  aria-label={`Add child under ${name}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Plus className="tw:h-3.5 tw:w-3.5" />
                </Button>
              }
            />
            <DropdownMenuContent
              align="start"
              side="left"
              className={cn(colorMode, 'tw:w-56')}
            >
              <DropdownMenuItem
                className="tw:cursor-pointer"
                onClick={() => onAddChildPosition(positionId)}
              >
                Add position
              </DropdownMenuItem>
              <DropdownMenuItem
                className="tw:cursor-pointer"
                onClick={() => onAddChildResource(positionId)}
              >
                Add resource
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      {showCollapse ? (
        <div className="tw:absolute tw:bottom-0 tw:left-1/2 tw:z-20 tw:flex tw:-translate-x-1/2 tw:translate-y-1/2 tw:items-center tw:gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="tw:h-5 tw:w-5 tw:rounded-full tw:border-border tw:bg-background tw:shadow-sm tw:hover:bg-accent"
            aria-label={
              collapsed
                ? `Expand seats under ${name}`
                : `Collapse seats under ${name}`
            }
            aria-expanded={!collapsed}
            title={collapsed ? 'Show seats below' : 'Hide seats below'}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onToggleCollapse(positionId);
            }}
          >
            <ChevronDown
              className={cn(
                'tw:h-3.5 tw:w-3.5 tw:transition-transform',
                collapsed && 'tw:-rotate-90'
              )}
            />
          </Button>
          {collapsed && hiddenCount > 0 ? (
            <Badge
              variant="secondary"
              className="tw:h-4 tw:px-1.5 tw:text-[9px]"
              title={`${hiddenCount} seat${hiddenCount === 1 ? '' : 's'} hidden`}
            >
              +{hiddenCount}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {isResourcePosition && (
        <Badge
          variant="secondary"
          className="tw:absolute tw:-top-2 tw:-right-2 tw:h-4 tw:px-1.5 tw:text-[9px]"
        >
          {resourceType ?? 'Resource'}
        </Badge>
      )}
      {/* Without this the pending retirement is invisible once the modal closes, and the seat just vanishes when
            the period flips. */}
      {retireNextOp && (
        <Badge
          variant="destructive"
          className="tw:absolute tw:-top-2 tw:-left-2 tw:h-4 tw:px-1.5 tw:text-[9px]"
          title="Leaves the roster at the start of the next operational period"
        >
          RETIRING
        </Badge>
      )}
      <CardHeader className="tw:gap-0">
        {name && (
          <CardTitle className="tw:truncate tw:text-sm tw:leading-tight tw:font-semibold">
            {name}
          </CardTitle>
        )}
        {hwcgStatusLine ? (
          <div
            data-hwcg-seat-status=""
            className="tw:mt-1 tw:text-[9px] tw:leading-tight tw:text-muted-foreground"
          >
            {hwcgStatusLine}
          </div>
        ) : null}
        {categoryLabel && (
          <CardDescription className={cn(SECONDARY_TEXT_CLASS, 'tw:truncate')}>
            {categoryLabel}
          </CardDescription>
        )}
      </CardHeader>
      {hasOccupantBody && (
        <CardContent className="tw:flex tw:flex-col tw:items-center tw:gap-0.5">
          {isUnfilled && <UnfilledLine />}
          {activeMembers.length > 0 && (
            <>
              {labelRosters && <RosterGroupLabel>Current OP</RosterGroupLabel>}
              <OccupantGroup
                positionId={positionId}
                isWorkgroup={isWorkgroup}
                members={activeMembers}
                periodLabels={periodLabels}
              />
            </>
          )}
          {activeMembers.length > 0 && draftMembers.length > 0 && (
            <div className="tw:my-0.5 tw:h-px tw:w-full tw:bg-border" />
          )}
          {draftMembers.length > 0 && (
            <>
              {labelRosters && <RosterGroupLabel>Next OP</RosterGroupLabel>}
              <OccupantGroup
                positionId={positionId}
                isWorkgroup={isWorkgroup}
                members={draftMembers}
                periodLabels={periodLabels}
              />
            </>
          )}
          {activeResourceCount > 0 && (
            <div className={SECONDARY_TEXT_CLASS}>
              {activeResourceCount} RESOURCE
              {activeResourceCount > 1 ? 'S' : ''}
            </div>
          )}
          {draftResourceCount > 0 && (
            <div className={SECONDARY_TEXT_CLASS}>
              {draftResourceCount} RESOURCE
              {draftResourceCount > 1 ? 'S' : ''} NEXT OP
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default SeatBoxCard;
